import { memoizeFunction } from '@agilepoint/utils-common';
import { PureComponent, createRef, ContextType } from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { IItemPositionData } from './types';
import { IListViewCommonProps, IListViewProps } from '../listview-types';
import { ListViewState } from '../theme';
import { IBaseComponentProps } from '../../types';
import { ResizeNotifierContext } from '../../../utils';

export interface IListViewItemProps extends IBaseComponentProps<HTMLDivElement>, Pick<IListViewCommonProps, 'onRenderItem' | 'onRenderItemContent'>, Required<Pick<IListViewProps, 'selectable'>> {
    data: any;
    height: number;
    isFocused: boolean;
    isHorizontallyAligned: boolean;
    isSelected: boolean;
    left: number;
    top: number;
    width: number;
    autoResizeThreshold?: number;
    onItemRectCapture: (item: any, positionInfo: IItemPositionData) => void
    onSelect(item: any): void;
}

interface IListviewItemContentWrapperProps extends Required<Pick<IListViewItemProps, 'isFocused' | 'isSelected' | 'selectable'>> {
}

const generateStyleByState = memoizeFunction((state: ListViewState) => {
    const getStyleObject = ({ listView: { colors: { item } } }: DefaultTheme) => item[state];
    return css`
        background-color: ${({ theme }) => getStyleObject(theme).backgroundColor};
        border-color: ${({ theme }) => getStyleObject(theme).borderColor};
        color: ${({ theme }) => getStyleObject(theme).foregroundColor};
    `;
});

const ListviewItemContentWrapper = styled.div.attrs({
    className: String('ap-listView-itemContentWrapper')
}) <IListviewItemContentWrapperProps>`
    height: 100%;
    width: 100%;
    border-style: solid;
    box-sizing: border-box;
    border-radius: ${({ theme: { listView: { composites: { item } } } }) => item.borderRadius};
    border-width: ${({ theme: { listView: { composites: { item } } } }) => item.borderWidth};
    box-shadow: ${({ theme: { listView: { composites: { item } } } }) => item.boxShadow};
    ${({ isSelected, isFocused, selectable }) => generateStyleByState(isSelected ? 'selected' : (selectable !== 'none' && isFocused ? 'focussed' : 'default'))}
    &:hover {
        ${({ isSelected, selectable }) => generateStyleByState(selectable === 'none' ? 'default' : (isSelected ? 'selected' : 'hovered'))}
    }
`;

class ListViewItemBase extends PureComponent<IListViewItemProps> {
    static contextType = ResizeNotifierContext;
    declare context: ContextType<typeof ResizeNotifierContext>;
    private itemDOMRef = createRef<HTMLDivElement>();

    render() {
        const { onRenderItem, className, height, width, top, left, isHorizontallyAligned } = this.props;
        return (
            <div ref={this.itemDOMRef} className={className} style={{
                height: isHorizontallyAligned ? '100%' : height || 'auto',
                width: isHorizontallyAligned ? width || 'auto' : '100%',
                top,
                left
            }}>
                {onRenderItem ? this.renderItem() : this.renderItemContent()}
            </div>
        );
    }

    componentDidMount() {
        const { current } = this.itemDOMRef;
        this.updateItemRect();
        if (!current?.firstChild) {
            return;
        }
        this.context.subscribe(current.firstChild, this.onResize);
    }

    componentWillUnmount() {
        const { current } = this.itemDOMRef;
        if (!current?.firstChild) {
            return;
        }
        this.context.unsubscribe(current.firstChild, this.onResize);
    }

    componentDidUpdate(prevProps: IListViewItemProps) {
        const { isFocused } = this.props;
        const { current } = this.itemDOMRef;
        if (!isFocused || isFocused === prevProps.isFocused || current === null) {
            return;
        }
        current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
        });
    }

    private renderItem() {
        const { isFocused, isSelected, data, onRenderItem, selectable } = this.props;
        if (!onRenderItem) {
            return null;
        }
        return onRenderItem({ data, isSelected, isFocused, toggleSelection: this.toggleSelection, renderDefault: this.renderItemContent, selectable, adjustDimensions: this.adjustDimensions });
    }

    private updateItemRect = () => {
        const { onItemRectCapture, data, isHorizontallyAligned } = this.props;
        const { current } = this.itemDOMRef;

        if (current !== null) {
            const rect = current.getBoundingClientRect();
            const spaceProperty = isHorizontallyAligned ? 'width' : 'height';
            if (rect[spaceProperty] === 0) {
                rect[spaceProperty] = isHorizontallyAligned ? current.offsetWidth : current.offsetHeight;
            }
            onItemRectCapture(data, rect);
        }
    }

    private onResize = (rect: Pick<DOMRect, 'height' | 'width'>) => {
        const { onItemRectCapture, data, isHorizontallyAligned, autoResizeThreshold = 4 } = this.props;
        const { current } = this.itemDOMRef;
        if (current === null) {
            return;
        }
        const domRect = current.getBoundingClientRect();
        const spaceProperty = isHorizontallyAligned ? 'width' : 'height';
        if (Math.abs(domRect[spaceProperty] - rect[spaceProperty]) < autoResizeThreshold) {
            return;
        }
        domRect[spaceProperty] = rect[spaceProperty];
        onItemRectCapture(data, domRect);
    }

    private adjustDimensions = () => {
        const { onItemRectCapture, data, isHorizontallyAligned } = this.props;
        const { current } = this.itemDOMRef;
        if (current === null) {
            return;
        }
        isHorizontallyAligned ? current.style.width = '0' : current.style.height = '0';
        const domRect = current.getBoundingClientRect();
        if (isHorizontallyAligned) {
            const updateWidth = current.scrollWidth;
            domRect.width = updateWidth;
        } else {
            const updateHeight = current.scrollHeight;
            domRect.height = updateHeight;
        }
        onItemRectCapture(data, domRect);
    }

    private renderItemContent = () => {
        const { isSelected, isFocused, data, onRenderItemContent, selectable } = this.props;
        if (!onRenderItemContent) {
            return null;
        }
        return (
            <ListviewItemContentWrapper selectable={selectable} isSelected={isSelected}
                isFocused={isFocused} onClick={this.onItemClick}>
                {onRenderItemContent(data)}
            </ListviewItemContentWrapper>
        );
    }

    private onItemClick = () => {
        this.toggleSelection();
    }

    private toggleSelection = () => {
        const { onSelect, selectable, data } = this.props;
        if (!selectable) {
            return;
        }
        onSelect(data);
    }
}

export const ListViewItem = styled(ListViewItemBase)`
    position: absolute;
    cursor: default;
`;
